import { Modal, ModalSize } from "@components/new/Modal";
import { useMutation } from "react-query";
import FormErrorMessage from "@components/FormErrorMessage";
import { H4, P3 } from "@components/new/Typo";
import { ButtonType } from "@components/new/Button";
import XIcon from "@assets/icons/solid/circle-x.svg?react";
import InfoIcon from "@assets/icons/solid/circle-info.svg?react";
import { useTranslation } from "react-i18next";
import styles from "./ConfirmModal.module.scss";

const icons: Record<string, React.ReactNode> = {
  "info": <InfoIcon className={styles.infoIcon} />,
}

interface ConfirmModalProps {
  title: string;
  description?: string | JSX.Element;
  yesBtnType?: ButtonType;
  yesBtnTitle?: string;
  noBtnTitle?: string;
  onClose: () => void;
  onSuccess: () => void;
  requestFn: () => any;
  icon?: JSX.Element;
  inModal?: boolean;
  type?: "info" | "error";
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  description,
  yesBtnType,
  yesBtnTitle,
  noBtnTitle,
  onClose,
  onSuccess,
  requestFn,
  icon,
  inModal,
  type,
}: ConfirmModalProps): React.ReactElement => {
  const { t } = useTranslation();

  const { error, isError, isLoading, mutate } = useMutation({
    mutationFn: () => requestFn(),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  return (
    <Modal
      size={ModalSize.S}
      isOpen={true}
      onClose={onClose}
      footerButtons={[
        {
          type: yesBtnType ?? ButtonType.ATTENTION,
          title: yesBtnTitle ?? t("confirmModal.yes"),
          onClick: () => mutate(),
          loading: isLoading,
        },
        {
          type: ButtonType.SECONDARY,
          title: noBtnTitle ?? t("confirmModal.no"),
          onClick: onClose,
        },
      ]}
      inModal={inModal}
    >
      <div className={styles.content}>
        <div className={styles.icon}>{icon || icons[type ?? ""] || <XIcon />}</div>
        <H4>{title}</H4>
        {description ? (
          <P3
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
        ) : null}
        {isError ? <FormErrorMessage error={error} /> : null}
      </div>
    </Modal>
  );
};

export default ConfirmModal;
